import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { useTranslate } from 'src/locales';

import DocumentViewer from 'src/components/documents/document-viewer';
import HighlightedText from 'src/components/highlight-text/highlight-text';

// ----------------------------------------------------------------------

/**
 * Display-only tidy of the parsed resume_text: joins a bullet marker the parser left stranded on its
 * own line onto the text that follows, and collapses runs of blank lines. Purely cosmetic — the
 * stored text is never modified.
 */
const tidyResumeText = (text: string): string =>
  text
    .replace(/^[ \t]*([•·‣▪])[ \t]*\r?\n+[ \t]*(?=\S)/gm, '$1 ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

interface Props {
  resumeText?: string | null;
  resumeDocUrl?: string | null;
  /** Search terms to highlight: on the PDF itself where the resume is one, otherwise in the parsed text. */
  highlightTerms?: string[];
  /**
   * Which view to show first when both a document and parsed text exist. Default `document` preserves
   * the uploaded CV's original formatting (record panel); the profile Resume tab passes `text`. A
   * highlight term forces text only for a non-PDF document: a Word file renders in Microsoft's viewer,
   * which this page cannot mark up, while a PDF takes the marks in its text layer.
   */
  prefer?: 'text' | 'document';
}

/**
 * Shared resume body (spec items 14/18; JT1-1529 / JT1-1537). Renders the parsed `resume_text` (with
 * optional search-term highlighting) or the uploaded document in a PDF viewer, falling back to an
 * empty message. Reused by the record-panel quick view and the candidate profile Resume tab so both
 * render resumes identically.
 */
export default function CandidateResumeView({ resumeText, resumeDocUrl, highlightTerms, prefer = 'document' }: Props) {
  const { t } = useTranslate();

  const terms = highlightTerms ?? [];
  const hasText = Boolean(resumeText);
  // The extension test DocumentViewer uses to pick its renderer, so a PDF here is one that gets the PDF viewer.
  const isPdf = resumeDocUrl?.split('.').pop()?.toLowerCase() === 'pdf';
  const showText = hasText && (prefer === 'text' || !resumeDocUrl || (terms.length > 0 && !isPdf));

  if (showText) {
    return (
      <Box
        sx={{
          whiteSpace: 'pre-wrap',
          typography: 'body2',
          lineHeight: 1.8,
          color: 'text.primary',
          p: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
        }}
      >
        <HighlightedText text={tidyResumeText(resumeText ?? '')} terms={terms} />
      </Box>
    );
  }

  if (resumeDocUrl) {
    // height:100% fills the flex record-panel body. On an auto-height parent (the profile Resume tab)
    // a percentage height has nothing to resolve against, so the Word viewer's iframe fell back to the
    // browser's 150px default and filled a fraction of the card. As a grid, minHeight sizes the row and
    // the viewer's height:100% resolves against it. A PDF's pages outgrow the row, so it still expands.
    return (
      <Box sx={{ height: '100%', minHeight: '70vh', display: 'grid' }}>
        {/* The caller's array, not `terms`: a fresh [] each render would re-render every PDF page. */}
        <DocumentViewer fileUrl={resumeDocUrl} highlightTerms={highlightTerms} />
      </Box>
    );
  }

  return (
    <Typography sx={{ py: 5 }} align="center" variant="body2">
      {t('candidates.drawer.no_resume')}
    </Typography>
  );
}
